import React from 'react';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import FONT_FAMILIES from '../constants/fonts';

// Inline font style controls for a block's section title.
// Mirrors the Section Header editor controls (font/size/weight/color/align)
// and writes to `content.titleStyle = { fontSize, fontFamily, fontWeight, color, align }`.
// If `styles` + `onStylesChange` are passed, also exposes a Background color swatch
// in the same row that writes to the block-level `styles.backgroundColor`.
const TitleStyleControls = ({ content, onChange, styles, onStylesChange, testIdPrefix = 'title' }) => {
  const ts = content.titleStyle || {};
  const update = (patch) =>
    onChange({ ...content, titleStyle: { ...ts, ...patch } });

  const showBg = typeof onStylesChange === 'function';
  const bgColor = (styles && styles.backgroundColor) || '';
  const updateBg = (val) => onStylesChange({ ...(styles || {}), backgroundColor: val });

  return (
    <div className="p-2 bg-slate-50 rounded-lg space-y-2" onClick={(e) => e.stopPropagation()}>
      <div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
        Title & Section
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <label className="text-xs text-slate-500">Size:</label>
          <Select
            value={ts.fontSize || '36'}
            onValueChange={(v) => update({ fontSize: v })}
          >
            <SelectTrigger className="w-16 h-7 text-xs" data-testid={`${testIdPrefix}-size`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {['14','16','18','20','24','28','32','36','42','48','56','64','72','76'].map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1">
          <label className="text-xs text-slate-500">Font:</label>
          <Select
            value={ts.fontFamily || 'inherit'}
            onValueChange={(v) => update({ fontFamily: v })}
          >
            <SelectTrigger className="w-28 h-7 text-xs" data-testid={`${testIdPrefix}-font`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FONT_FAMILIES.map((font) => (
                <SelectItem key={font.value} value={font.value} style={{ fontFamily: font.value }}>
                  {font.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1">
          <label className="text-xs text-slate-500">Weight:</label>
          <Select
            value={ts.fontWeight || 'bold'}
            onValueChange={(v) => update({ fontWeight: v })}
          >
            <SelectTrigger className="w-20 h-7 text-xs" data-testid={`${testIdPrefix}-weight`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="500">Medium</SelectItem>
              <SelectItem value="600">Semi</SelectItem>
              <SelectItem value="bold">Bold</SelectItem>
              <SelectItem value="800">Extra</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1">
          <label className="text-xs text-slate-500">Color:</label>
          <input
            type="color"
            value={ts.color || '#1e293b'}
            onChange={(e) => update({ color: e.target.value })}
            className="w-7 h-7 rounded border cursor-pointer"
            data-testid={`${testIdPrefix}-color`}
          />
          {ts.color && (
            <button
              type="button"
              onClick={() => update({ color: '' })}
              className="text-[10px] text-slate-400 hover:text-slate-600 underline"
              title="Use theme color"
            >
              reset
            </button>
          )}
        </div>

        <div className="flex items-center gap-1">
          <label className="text-xs text-slate-500">Align:</label>
          <Select
            value={ts.align || 'center'}
            onValueChange={(v) => update({ align: v })}
          >
            <SelectTrigger className="w-20 h-7 text-xs" data-testid={`${testIdPrefix}-align`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="left">Left</SelectItem>
              <SelectItem value="center">Center</SelectItem>
              <SelectItem value="right">Right</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {showBg && (
          <div className="flex items-center gap-1 border-l border-slate-300 pl-3 ml-1">
            <label className="text-xs text-slate-500">Background:</label>
            <input
              type="color"
              value={bgColor && bgColor.startsWith('#') ? bgColor : '#ffffff'}
              onChange={(e) => updateBg(e.target.value)}
              className="w-7 h-7 rounded border cursor-pointer"
              data-testid={`${testIdPrefix}-bg`}
            />
            {bgColor && (
              <button
                type="button"
                onClick={() => updateBg('')}
                className="text-[10px] text-slate-400 hover:text-slate-600 underline"
                title="Clear background"
              >
                clear
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TitleStyleControls;
